import { useEffect, useRef, useState, Suspense } from 'react';

/**
 * <LazyOnVisible>
 *   Delays mounting a child until the wrapper enters (or nears) the viewport.
 *   Purpose: cut TBT / TTI. When 12+ below-the-fold sections are all wrapped
 *   in a single top-level <Suspense>, React starts downloading and hydrating
 *   every lazy chunk at once during the initial render — that hammers the
 *   main thread even though the user can't see any of it.
 *
 *   With this wrapper each section sleeps as an empty placeholder until
 *   the IntersectionObserver reports it's close to the viewport, THEN
 *   we render the real component (still Suspense-lazy). API calls the
 *   section makes on mount also delay to that point.
 *
 *   Safety:
 *     • Above-the-fold content is unaffected — this only wraps below-fold.
 *     • rootMargin defaults to a generous 400px so the section is ready
 *       before the user visually reaches it (no perceived jank).
 *     • Falls back to immediate render on the ~2% of browsers without IO,
 *       and on the first `<noscript>` snapshot server-side crawlers see.
 *     • `minHeight` prevents CLS while the placeholder is still empty —
 *       pass the approximate section height so the scrollbar doesn't jump.
 */
export default function LazyOnVisible({
  children,
  rootMargin = '400px',
  minHeight = 1,
  fallback = null,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // SSR-safe: only browsers with IntersectionObserver participate.
    if (typeof window === 'undefined') return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin, threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} style={{ minHeight: visible ? undefined : minHeight }}>
      {visible ? <Suspense fallback={<div style={{ minHeight }}>{fallback}</div>}>{children}</Suspense> : null}
    </div>
  );
}
